import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CountUp from "react-countup";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { Reveal } from "@/components/Reveal";
import { PlayButton, VideoModal } from "@/components/VideoModal";
import { Testimonials } from "@/components/Testimonials";
import { CTABanner } from "@/components/CTABanner";
import { RecruitmentBanner } from "@/components/RecruitmentBanner";
import { HeroCarousel } from "@/components/HeroCarousel";
import { useSite, mediaUrl, phoneHref } from "@/context/SiteContext";
import { SERVICES, VALUES, COUNTIES, FACILITY_BENEFITS, IMAGES, ODP_SERVICES } from "@/lib/data";

const STATS = [
  { end: 10, suffix: "", label: "Pennsylvania Counties Served" },
  { end: 4, suffix: "", label: "Qualified ODP Services" },
  { end: 6, suffix: "", label: "Transportation Service Types" },
  { icon: "Heart", label: "Person-Centered Approach" },
];

export default function Home() {
  const navigate = useNavigate();
  const content = useSite();
  const [heroVideo, setHeroVideo] = useState(false);
  const [odpVideo, setOdpVideo] = useState(false);
  const [hoverCounty, setHoverCounty] = useState(null);
  const pic = (id, fb) => (id ? mediaUrl(id) : fb);

  const heroSrc = pic(content.hero_image_id, IMAGES.hero);
  const facilitiesSrc = pic(content.facilities_image_id, IMAGES.facilities);
  const careSrc = pic(content.care_image_id, IMAGES.care);
  const odpVideoSrc = pic(content.odp_video_image_id, IMAGES.odpCommunity);
  const odpImgA = pic(content.odp_companion_image_id, IMAGES.odpCompanion);
  const odpImgB = pic(content.odp_nursing_image_id, IMAGES.odpNursing);

  const bySlug = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));
  const transport = [
    { s: bySlug["nemt"], img: pic(content.nemt_image_id, IMAGES.nemt) },
    { s: bySlug["wheelchair-transportation"], img: pic(content.wheelchair_image_id, IMAGES.wheelchair) },
    { s: bySlug["ambulatory-transportation"], img: pic(content.ambulatory_image_id, IMAGES.ambulatory) },
  ].filter((t) => t.s);

  return (
    <>
      <VideoModal open={heroVideo} onOpenChange={setHeroVideo} videoUrl={content.hero_video_url} />
      <VideoModal open={odpVideo} onOpenChange={setOdpVideo} videoUrl={content.odp_video_url} />

      {/* HERO */}
      <HeroCarousel content={content} heroSrc={heroSrc} odpSrc={odpVideoSrc} onPlayVideo={() => setHeroVideo(true)} />

      {/* RETURN RIDE STRIP */}
      <section className="bg-brand-teal" data-testid="return-ride-strip">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 px-4 sm:px-6 lg:px-8 py-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <Icon name="Phone" size={22} weight="fill" className="mt-0.5 shrink-0 text-white" />
            <div>
              <p className="font-heading text-base font-semibold text-white sm:text-lg">Already riding with CareDeserved?</p>
              <p className="text-sm text-teal-50 sm:text-base">
                When your appointment is complete, please call the driver assigned to your trip for your return ride.
              </p>
            </div>
          </div>
          <p className="text-sm text-teal-50 sm:text-base md:text-right">
            Can't reach your driver? Call our main office:{" "}
            <a href={phoneHref(content.phone)} data-testid="return-ride-phone" className="font-bold text-white underline underline-offset-2">{content.phone}</a>
          </p>
        </div>
      </section>

      {/* TRANSPORTATION SERVICES — photo cards */}
      <section className="bg-white py-20 sm:py-28" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-xl">
              <p className="eyebrow text-brand-purple">Our Transportation Services</p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                More Than Transportation.<br />We're Here to Serve.
              </h2>
            </div>
            <Link to="/services" data-testid="explore-all-services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-purple hover:text-brand-purple2">
              Explore All Services <Icon name="ArrowRight" size={16} weight="bold" />
            </Link>
          </div>
          <div className="mt-12 grid gap-7 md:grid-cols-3">
            {transport.map(({ s, img }, i) => (
              <Reveal key={s.slug} delay={i * 0.08}>
                <Link to={s.path} data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
                  <div className="relative h-52 overflow-hidden">
                    <img src={img} alt={s.title} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute -bottom-5 left-6 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-purple text-white shadow-lg">
                      <Icon name={s.icon} size={22} weight="fill" />
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6 pt-8">
                    <h3 className="font-heading text-lg font-semibold text-slate-900">{s.title}</h3>
                    <p className="mt-2.5 flex-1 text-[15px] leading-relaxed text-slate-600">{s.card}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-purple">
                      Learn More <Icon name="ArrowRight" size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ODP SERVICES — visual storytelling */}
      <RecruitmentBanner />

      <section className="bg-brand-purple/[0.04] py-20 sm:py-28" data-testid="odp-home-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
            {/* Text */}
            <div className="lg:col-span-4">
              <p className="eyebrow text-brand-purple">ODP Services</p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Supporting Independence Beyond Transportation
              </h2>
              <p className="mt-5 text-slate-600 md:text-lg">
                As a qualified Pennsylvania ODP provider, CareDeserved offers Home &amp; Community Habilitation,
                Companion Services, Registered Nursing, and Transportation that empower individuals to live, learn,
                work, and thrive.
              </p>
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-purple/10 px-4 py-2 text-sm font-semibold text-brand-purple" data-testid="home-odp-statewide">
                <Icon name="MapPinLine" size={16} weight="fill" /> Statewide ODP referrals welcome
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <Link to="/odp-services">
                  <Button data-testid="home-odp-explore" className="h-13 w-full rounded-full bg-brand-purple px-7 py-3 font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-brand-purple2 sm:w-auto">
                    Explore ODP Services
                  </Button>
                </Link>
                <Link to="/odp-services">
                  <Button data-testid="home-odp-refer" variant="outline" className="h-13 w-full rounded-full border-brand-purple px-7 py-3 font-semibold text-brand-purple hover:bg-brand-purple hover:text-white sm:w-auto">
                    Make an ODP Referral
                  </Button>
                </Link>
              </div>
            </div>

            {/* Media bento */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-3 grid-rows-2 gap-3" style={{ height: "340px" }}>
                <button type="button" onClick={() => setOdpVideo(true)} data-testid="odp-play"
                  className="group relative col-span-2 row-span-2 overflow-hidden rounded-2xl focus:outline-none">
                  <img src={odpVideoSrc} alt="An individual enjoying a community art activity" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-brand-blue/25 transition-colors group-hover:bg-brand-blue/35" />
                  <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-teal shadow-2xl ring-4 ring-white/40 transition-transform duration-300 group-hover:scale-110">
                    <Icon name="Play" weight="fill" size={26} className="ml-1" />
                  </span>
                  <span className="absolute bottom-3 left-3 rounded-full bg-brand-blue/70 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">See Care Beyond the Ride</span>
                </button>
                <div className="overflow-hidden rounded-2xl">
                  <img src={odpImgA} alt="A support professional and an individual enjoying conversation" className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="overflow-hidden rounded-2xl">
                  <img src={odpImgB} alt="A compassionate nurse supporting an individual at home" className="h-full w-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>

            {/* Service list */}
            <div className="lg:col-span-3">
              <ul className="space-y-4">
                {ODP_SERVICES.map((s) => (
                  <li key={s.title} className="flex items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-purple/10 text-brand-purple">
                      <Icon name={s.icon} size={22} weight="duotone" />
                    </span>
                    <span className="font-heading text-[15px] font-semibold text-slate-800">{s.title}</span>
                  </li>
                ))}
              </ul>
              <Link to="/odp-services" data-testid="home-odp-viewall" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-purple hover:text-brand-purple2">
                View All ODP Services <Icon name="ArrowRight" size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE + STATS — dark blue */}
      <section className="relative overflow-hidden bg-brand-blue noise-overlay py-20 sm:py-28" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="stats-band">
            {STATS.map((st) => (
              <div key={st.label} className="text-center">
                {st.icon ? (
                  <span className="flex h-11 items-center justify-center sm:h-14">
                    <Icon name={st.icon} weight="fill" size={44} className="text-brand-teal2" />
                  </span>
                ) : (
                  <p className="font-heading text-4xl font-extrabold text-brand-teal2 sm:text-5xl">
                    <CountUp end={st.end} suffix={st.suffix} duration={2} enableScrollSpy scrollSpyOnce />
                  </p>
                )}
                <p className="mt-2 text-sm font-medium text-slate-300">{st.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="group overflow-hidden rounded-3xl shadow-xl ring-1 ring-white/10">
                <img src={careSrc} data-testid="why-choose-image"
                  alt="A CareDeserved caregiver compassionately supporting an individual"
                  className="h-[320px] w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-[400px]" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="eyebrow text-brand-teal2">Why Choose CareDeserved</p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">Care Beyond the Ride.</h2>
              <p className="mt-5 text-base leading-relaxed text-slate-300 md:text-lg">
                We understand that care is about more than reaching a destination. It is about helping the people we serve
                feel safe, respected, comfortable, and supported — on every ride and in every service we provide.
              </p>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm transition-colors hover:bg-white/[0.08]">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-teal/20 text-brand-teal2">
                    <Icon name={v.icon} size={30} weight="duotone" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-white py-20 sm:py-28" data-testid="service-areas-home">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
              <p className="eyebrow text-brand-purple">Service Areas</p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {content.service_areas_heading}
              </h2>
              <p className="mt-5 text-slate-600 md:text-lg">{content.service_areas_text}</p>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {COUNTIES.map((c) => (
                  <Link key={c} to={`/service-areas/${c.toLowerCase()}-county-pa`}
                    data-testid={`county-chip-${c.toLowerCase()}`}
                    onMouseEnter={() => setHoverCounty(c)} onMouseLeave={() => setHoverCounty(null)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${hoverCounty === c ? "border-brand-purple bg-brand-purple text-white" : "border-slate-200 bg-brand-bg text-brand-blue hover:border-brand-purple hover:text-brand-purple"}`}>
                    {c} County
                  </Link>
                ))}
              </div>
              <Link to="/service-areas">
                <Button data-testid="view-service-areas" variant="outline" className="mt-8 h-12 rounded-full border-brand-blue px-6 font-semibold text-brand-blue hover:bg-brand-blue hover:text-white">
                  View All Service Areas
                </Button>
              </Link>
            </div>
            <div className="relative"><ServiceAreaMap hovered={hoverCounty} setHovered={setHoverCounty} /></div>
          </div>
        </div>
      </section>

      {/* FOR FACILITIES */}
      <section className="bg-brand-bg py-20 sm:py-28" data-testid="for-facilities-home">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img src={facilitiesSrc} alt="Modern healthcare facility partnering with CareDeserved" className="h-[360px] w-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="eyebrow text-brand-purple">For Facilities &amp; Partners</p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                A Transportation Partner You Can Depend On
              </h2>
              <p className="mt-5 text-slate-600 md:text-lg">
                CareDeserved works with healthcare facilities, treatment centers, senior communities, day programs,
                agencies, case managers, and other organizations that need dependable passenger transportation.
              </p>
              <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                {FACILITY_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Icon name="CheckCircle" size={20} weight="fill" className="mt-0.5 shrink-0 text-brand-teal" /> {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/facilities">
                  <Button data-testid="facilities-become-partner" className="h-13 w-full rounded-full bg-brand-blue px-7 py-3 font-semibold text-white hover:bg-brand-blue2 sm:w-auto">
                    Become a Transportation Partner
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button data-testid="facilities-request-info" variant="outline" className="h-13 w-full rounded-full border-brand-blue px-7 py-3 font-semibold text-brand-blue hover:bg-brand-blue hover:text-white sm:w-auto">
                    Request Information
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />
      <CTABanner />
    </>
  );
}

function ServiceAreaMap({ hovered, setHovered }) {
  const navigate = useNavigate();
  const points = [
    [305, 95], [324, 114], [288, 124], [307, 140], [270, 102],
    [256, 134], [240, 112], [226, 140], [210, 120], [196, 144],
  ];
  return (
    <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-brand-blue to-brand-blue2 p-6 shadow-xl noise-overlay" data-testid="service-area-map">
      <svg viewBox="0 0 400 235" className="relative z-10 h-auto w-full" role="img" aria-label="CareDeserved Pennsylvania service area map">
        <path d="M20 40 L360 30 L380 60 L378 150 L360 170 L300 185 L60 190 L30 160 Z"
          fill="#ffffff" fillOpacity="0.06" stroke="#0a9797" strokeWidth="2" strokeOpacity="0.55" />
        {COUNTIES.map((c, i) => {
          const [x, y] = points[i] || [200, 110];
          const active = hovered === c;
          return (
            <g key={c} className="cursor-pointer" role="link" aria-label={`${c} County`}
              onMouseEnter={() => setHovered(c)} onMouseLeave={() => setHovered(null)}
              onClick={() => navigate(`/service-areas/${c.toLowerCase()}-county-pa`)}>
              <circle cx={x} cy={y} r={active ? 17 : 11} fill="#0a9797" fillOpacity={active ? 0.28 : 0.14} className="transition-all duration-200" />
              <circle cx={x} cy={y} r={active ? 8 : 5.5} fill={active ? "#0a9797" : "#7c53b8"} className="transition-all duration-200" />
              {active && (
                <g>
                  <rect x={x - 52} y={y - 34} width="104" height="21" rx="10" fill="#ffffff" />
                  <text x={x} y={y - 19.5} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#0F2C59" fontFamily="Outfit, sans-serif">{c} County</text>
                </g>
              )}
            </g>
          );
        })}
        <text x="120" y="78" fontFamily="Outfit, sans-serif" fontSize="14" fontWeight="700" fill="#ffffff" fillOpacity="0.9">Pennsylvania</text>
      </svg>
      <p className="relative z-10 mt-2 text-center text-xs text-teal-100/80">Hover or tap a marker to explore each county • Serving eastern &amp; central Pennsylvania</p>
    </div>
  );
}
